import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { catchError, map, of } from 'rxjs';
import {
  ConditionPayload,
  EntryPayloadCacheService,
} from '@core/services/entry-payload-cache.service';

function joinUrl(base: string, path: string): string {
  const b = base.replace(/\/+$/, '');
  const p = path.replace(/^\/+/, '');
  return `${b}/${p}`;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

function isConditionPayload(x: unknown): x is ConditionPayload {
  return isRecord(x) && 'condition' in x;
}

export const conditionResolver: ResolveFn<ConditionPayload> = (
  route: ActivatedRouteSnapshot,
) => {
  const http = inject(HttpClient);
  const cache = inject(EntryPayloadCacheService);

  const module = route.paramMap.get('module')?.trim() ?? '';
  const slug = route.paramMap.get('slug')?.trim() ?? '';

  const cached = module && slug ? cache.get(module, slug) : null;
  if (cached) return of(cached);

  // Fallback (should be rare because guard should have populated cache)
  const staticBase = (environment.staticBasePath ?? '/assets/v1').toString();
  const conditionUrl = joinUrl(staticBase, `conditions/${slug}.json`);

  return http.get<unknown>(conditionUrl).pipe(
    map((res) => {
      if (!isConditionPayload(res)) return { condition: null };
      const payload = res as ConditionPayload;
      return payload;
    }),
    catchError(() => of({ condition: null })),
  );
};
